import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useState } from 'react';
import { 
  House, 
  Tray, 
  Lightbulb, 
  Link as LinkIcon, 
  CalendarBlank,
  SignOut,
  List,
  X,
  Bell,
  Plus,
  MagnifyingGlass,
  Robot,
  Lightning
} from '@phosphor-icons/react';
import { useNavigate, NavLink } from 'react-router-dom';
import { NotificationPanel } from './NotificationPanel';

const sidebarItems = [
  { path: '/dashboard', label: 'Dashboard', icon: House },
  { path: '/inbox', label: 'Inbox', icon: Tray },
  { path: '/assistant', label: 'Assistant', icon: Robot },
  { path: '/calendar', label: 'Calendar', icon: CalendarBlank },
  { path: '/insights', label: 'Insights', icon: Lightbulb },
  { path: '/agents', label: 'Routines', icon: Lightning },
  { path: '/connections', label: 'Connections', icon: LinkIcon },
];

export default function Layout({ children }) {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f9f9fb] flex items-center justify-center">
        <div className="surface-card p-8 text-center">
          <div className="animate-spin w-12 h-12 border-4 border-[#ebeef2] border-t-[#005bc1] rounded-full mx-auto mb-4"></div>
          <p className="title-md">Loading LifeAdmin...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#f9f9fb]">
      {/* Mobile Header */}
      <header
        className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-nav mx-4 mt-4 px-4 py-3 flex items-center justify-between"
        style={{ marginTop: 'calc(env(safe-area-inset-top) + 1rem)' }}
      >
        <button 
          onClick={() => setSidebarOpen(true)}
          className="p-2 hover:bg-[#f2f4f6] rounded-xl transition-colors"
          data-testid="mobile-menu-btn"
        >
          <List size={24} weight="bold" className="text-[#2d3338]" />
        </button>
        <h1 className="font-bold text-lg text-[#2d3338]">LifeAdmin</h1>
        <div className="relative">
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            className="p-2 hover:bg-[#f2f4f6] rounded-xl relative transition-colors"
            data-testid="mobile-notification-btn"
          >
            <Bell size={24} weight="bold" className="text-[#2d3338]" />
            <span className="absolute top-2 right-2 w-2 h-2 bg-[#005bc1] rounded-full"></span>
          </button>
          <NotificationPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-50"
          onClick={() => setSidebarOpen(false)}
        >
          <div 
            className="w-72 h-full bg-white p-6 animate-fade-in"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-bold text-xl text-[#2d3338]">LifeAdmin</h1>
                <p className="label-md mt-1">AI Chief of Staff</p>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="p-2 hover:bg-[#f2f4f6] rounded-xl">
                <X size={24} weight="bold" className="text-[#596065]" />
              </button>
            </div>

            <nav className="space-y-1">
              {sidebarItems.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon size={22} weight={location.pathname === item.path ? 'fill' : 'regular'} />
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="absolute bottom-6 left-6 right-6">
              <button
                onClick={() => { setSidebarOpen(false); navigate('/inbox'); }}
                className="btn-primary w-full flex items-center justify-center gap-2 mb-4"
                data-testid="new-task-btn"
              >
                <Plus size={20} weight="bold" />
                New Task
              </button>
              
              <div className="flex items-center gap-3 p-3 bg-[#f2f4f6] rounded-xl">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#005bc1] to-[#004faa] flex items-center justify-center text-white font-semibold">
                    {user.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#2d3338] truncate">{user.name}</p>
                  <p className="text-xs text-[#596065] truncate">{user.email}</p>
                </div>
                <button onClick={handleLogout} className="p-2 hover:bg-[#ebeef2] rounded-lg transition-colors" data-testid="logout-btn">
                  <SignOut size={20} weight="bold" className="text-[#596065]" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-72 bg-white flex-col p-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-bold text-xl text-[#2d3338]">LifeAdmin</h1>
            <p className="label-md mt-1">AI Chief of Staff</p>
          </div>
          <div className="relative">
            <button
              onClick={() => setNotifOpen(!notifOpen)}
              className="p-2 hover:bg-[#f2f4f6] rounded-xl relative transition-colors"
              data-testid="desktop-notification-btn"
            >
              <Bell size={20} weight="bold" className="text-[#596065]" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#005bc1] rounded-full"></span>
            </button>
            <NotificationPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#596065]" />
          <input 
            type="text" 
            placeholder="Search tasks..." 
            className="w-full pl-11 pr-4 py-3 bg-[#f2f4f6] rounded-xl text-sm focus:bg-[#ebeef2] transition-colors outline-none"
          />
        </div>

        <nav className="space-y-1 flex-1">
          {sidebarItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon size={22} weight={location.pathname === item.path ? 'fill' : 'regular'} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto">
          <button
            onClick={() => navigate('/inbox')}
            className="btn-primary w-full flex items-center justify-center gap-2 mb-4"
            data-testid="new-task-btn-desktop"
          >
            <Plus size={20} weight="bold" />
            New Task
          </button>
          
          <div className="flex items-center gap-3 p-3 bg-[#f2f4f6] rounded-xl">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#005bc1] to-[#004faa] flex items-center justify-center text-white font-semibold">
                {user.name?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#2d3338] truncate">{user.name}</p>
              <p className="text-xs text-[#596065] truncate">{user.email}</p>
            </div>
            <button onClick={handleLogout} className="p-2 hover:bg-[#ebeef2] rounded-lg transition-colors" data-testid="logout-btn-desktop">
              <SignOut size={20} weight="bold" className="text-[#596065]" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72 pt-20 lg:pt-0 min-h-screen safe-main">
        {children}
      </main>
    </div>
  );
}
